// ─── CustomerSitesView — gestão de Postos (Petrotec Angola) ───────────────
//
// Drilldown de um FinalCustomer (cliente final) → lista dos seus Postos
// de Abastecimento (PAs).  Acedido via FinalCustomersView → botão "Postos".
//
// O id+nome do cliente final vem do Redux (`selectedFinalCustomer`) — assim
// sobrevive a refreshes da página enquanto este ecrã estiver activo. Se for
// null (entrar directamente por URL ou desync), redireccionamos para a
// lista de clientes finais em vez de mostrar o ecrã "órfão".

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useFormCardScroll } from '../../utils/manageHelpers';
import {
  Box, Typography, Button, TextField, IconButton, Tooltip, Chip, Switch,
  FormControlLabel, CircularProgress, Select, MenuItem, FormControl, InputLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import RoomIcon from '@material-ui/icons/Room';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import BuildIcon from '@material-ui/icons/Build';
import { useDispatch, useSelector } from 'react-redux';
import { selectUI, setActiveView, setSelectedFinalCustomer, setSelectedCustomerSite } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';
import ConfirmDialog from '../common/ConfirmDialog';
import AddressAutocomplete from '../common/AddressAutocomplete';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
    background: '#f8fafc', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
  },
  titleBox: { flex: 1, minWidth: 0 },
  breadcrumb: {
    fontSize: '0.68rem', color: '#64748b', fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap',
  },
  crumbLink: { cursor: 'pointer', color: '#dc2626', '&:hover': { textDecoration: 'underline' } },
  title: {
    fontSize: '1.05rem', fontWeight: 800, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  toolbar: {
    display: 'flex', gap: 8, padding: '12px 24px', alignItems: 'center',
    borderBottom: '1px solid #f1f5f9', background: '#fff', flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1, maxWidth: 360,
    '& .MuiOutlinedInput-root': { borderRadius: 8, background: '#fff', fontSize: '0.78rem' },
  },
  addButton: {
    background: '#3b82f6',
    color: '#fff', borderRadius: 8, textTransform: 'none', fontWeight: 700,
    padding: '6px 16px', fontSize: '0.74rem', boxShadow: 'none',
    '&:hover': { background: '#2563eb', boxShadow: '0 2px 6px rgba(59,130,246,0.25)' },
  },
  saveButton: {
    background: '#3b82f6', color: '#fff',
    textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
    padding: '6px 18px', boxShadow: 'none',
    '&:hover': { background: '#2563eb' },
  },
  list: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  card: {
    background: '#fff', borderRadius: 12, padding: '14px 18px', marginBottom: 10,
    border: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'center', gap: 14,
    transition: 'all 0.2s',
    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' },
  },
  cardInactive: { opacity: 0.55, background: '#f8fafc' },
  cardIcon: {
    width: 40, height: 40, borderRadius: 10, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    background: '#e0f2fe',
  },
  cardMain: { flex: 1, minWidth: 0 },
  cardName: {
    fontSize: '0.92rem', fontWeight: 700, color: '#0f172a',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  cardMeta: {
    fontSize: '0.7rem', color: '#64748b', marginTop: 4,
    display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
  },
  cardActions: { display: 'flex', gap: 4, alignItems: 'center' },
  empty: {
    padding: '80px 24px', textAlign: 'center', color: '#94a3b8',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  },
  formCard: {
    background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  formTitle: {
    fontSize: '0.86rem', fontWeight: 700, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  coordsHint: {
    fontSize: '0.66rem', color: '#16a34a', fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: 4, marginTop: 4,
  },
  formRow: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
  },
  formRow3: {
    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10,
  },
  formActions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
}));

const EMPTY_FORM = { finalCustomerId: '', name: '', code: '', address: '', lat: '', lng: '', notes: '' };

function CustomerSitesView({ lang = 'pt' }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const { selectedFinalCustomer } = useSelector(selectUI);
  const pt = lang === 'pt';

  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]); // só usado em modo "lista geral" (sem drilldown)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [filterFC, setFilterFC] = useState(''); // filtro de cliente final em modo lista geral
  const [editingId, setEditingId] = useState(null);
  const formCardRef = useFormCardScroll(editingId);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Modo do ecrã: 'drilldown' quando viemos a partir de um cliente final
  // específico (selectedFinalCustomer != null); 'list' caso contrário,
  // mostrando todos os postos do tenant com chip do cliente final.
  const fcId = selectedFinalCustomer?.id;
  const fcName = selectedFinalCustomer?.name || '';
  const mode = fcId ? 'drilldown' : 'list';

  // Em modo 'list', carregar a lista de clientes finais para o filtro/form
  useEffect(() => {
    if (mode !== 'list' || !token) return;
    fetch('/planner-api/final-customers', { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setCustomers(Array.isArray(data) ? data : []))
      .catch(() => setCustomers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, token]);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (mode === 'drilldown') params.set('finalCustomerId', String(fcId));
      else if (filterFC) params.set('finalCustomerId', String(filterFC));
      if (showInactive) params.set('includeInactive', '1');
      const qs = params.toString();
      const r = await fetch(`/planner-api/customer-sites${qs ? `?${qs}` : ''}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setSites(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('CustomerSites fetch error:', e);
      setError(pt ? 'Não foi possível carregar a lista.' : 'Could not load list.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, mode, fcId, filterFC, showInactive, pt]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  function startCreate() {
    setForm({
      ...EMPTY_FORM,
      // Em drilldown o cliente final já está fixado; em modo lista o operador escolhe
      finalCustomerId: mode === 'drilldown' ? fcId : (filterFC || ''),
    });
    setEditingId('new');
  }

  function startEdit(s) {
    setForm({
      finalCustomerId: s.finalCustomerId,
      name: s.name || '',
      code: s.code || '',
      address: s.address || '',
      lat: s.lat != null ? String(s.lat) : '',
      lng: s.lng != null ? String(s.lng) : '',
      notes: s.notes || '',
    });
    setEditingId(s.id);
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  async function saveForm() {
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError(pt ? 'Nome obrigatório (mínimo 2 caracteres).' : 'Name required (min 2 chars).');
      return;
    }
    if (!form.finalCustomerId) {
      setError(pt ? 'Cliente final obrigatório.' : 'Final customer required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = JSON.stringify({
        finalCustomerId: form.finalCustomerId,
        name: form.name.trim(),
        code: form.code.trim() || null,
        address: form.address.trim() || null,
        lat: form.lat.trim() || null,
        lng: form.lng.trim() || null,
        notes: form.notes.trim() || null,
      });
      const isNew = editingId === 'new';
      const url = isNew
        ? '/planner-api/customer-sites'
        : `/planner-api/customer-sites/${editingId}`;
      const method = isNew ? 'POST' : 'PUT';
      const r = await fetch(url, { method, headers, body });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
      await fetchAll();
    } catch (e) {
      setError(e.message || (pt ? 'Erro ao guardar.' : 'Save error.'));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(s) {
    try {
      const r = await fetch(`/planner-api/customer-sites/${s.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ isActive: !s.isActive }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchAll();
    } catch (e) {
      setError(e.message);
    }
  }

  async function confirmDeleteNow() {
    if (!deleteConfirm) return;
    try {
      const r = await fetch(`/planner-api/customer-sites/${deleteConfirm.id}`, {
        method: 'DELETE', headers,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDeleteConfirm(null);
      await fetchAll();
    } catch (e) {
      setError(e.message);
      setDeleteConfirm(null);
    }
  }

  function goBackToCustomers() {
    dispatch(setSelectedFinalCustomer(null));
    dispatch(setActiveView('finalCustomers'));
  }

  const filtered = sites.filter(s => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [s.name, s.code, s.address].some(v =>
      v && String(v).toLowerCase().includes(q),
    );
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Tooltip title={mode === 'drilldown'
          ? (pt ? 'Voltar a Clientes Finais' : 'Back to Final Customers')
          : (pt ? 'Voltar a Manutenção' : 'Back to Maintenance')}>
          <IconButton
            size="small"
            onClick={mode === 'drilldown' ? goBackToCustomers : () => dispatch(setActiveView('maintenance'))}
          >
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Box className={classes.titleBox}>
          <Box className={classes.breadcrumb}>
            <span className={classes.crumbLink} onClick={() => dispatch(setActiveView('maintenance'))}>
              {pt ? 'Manutenção' : 'Maintenance'}
            </span>
            {mode === 'drilldown' && (
              <>
                <span>›</span>
                <span className={classes.crumbLink} onClick={goBackToCustomers}>
                  {pt ? 'Clientes Finais' : 'Final Customers'}
                </span>
                <span>›</span>
                <span style={{ fontWeight: 700, color: '#0f172a' }}>{fcName}</span>
              </>
            )}
            {mode === 'list' && (
              <>
                <span>›</span>
                <span style={{ fontWeight: 700, color: '#0f172a' }}>{pt ? 'Postos' : 'Sites'}</span>
              </>
            )}
          </Box>
          <Typography className={classes.title}>
            <LocalGasStationIcon style={{ fontSize: 20, color: '#0ea5e9' }} />
            {pt ? 'Postos' : 'Sites'}
          </Typography>
        </Box>
        <Tooltip title={pt ? 'Recarregar' : 'Refresh'}>
          <IconButton size="small" onClick={fetchAll} disabled={loading}>
            <RefreshIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className={classes.toolbar}>
        <TextField
          className={classes.searchInput}
          size="small" variant="outlined"
          placeholder={pt ? 'Procurar por nome, código, morada…' : 'Search by name, code, address…'}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {mode === 'list' && (
          <FormControl size="small" variant="outlined" style={{ minWidth: 200 }}>
            <InputLabel>{pt ? 'Cliente final' : 'Final customer'}</InputLabel>
            <Select
              label={pt ? 'Cliente final' : 'Final customer'}
              value={filterFC}
              onChange={e => setFilterFC(e.target.value)}
            >
              <MenuItem value="">{pt ? 'Todos' : 'All'}</MenuItem>
              {customers.filter(c => c.isActive).map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
            />
          }
          label={
            <Typography style={{ fontSize: '0.75rem', color: '#475569' }}>
              {pt ? 'Mostrar desactivados' : 'Show inactive'}
            </Typography>
          }
        />
        <Box style={{ flex: 1 }} />
        <Button
          className={classes.addButton}
          startIcon={<AddIcon style={{ fontSize: 18 }} />}
          onClick={startCreate}
          disabled={editingId !== null}
        >
          {pt ? 'Novo posto' : 'New site'}
        </Button>
      </Box>

      <Box className={classes.list}>
        {error && (
          <Box style={{
            padding: '10px 14px', background: '#fef2f2',
            border: '1px solid #fecaca', color: '#991b1b',
            borderRadius: 8, marginBottom: 12, fontSize: '0.78rem',
          }}>
            {error}
          </Box>
        )}

        {editingId !== null && (
          <Box ref={formCardRef} className={classes.formCard}>
            <Typography className={classes.formTitle}>
              <LocalGasStationIcon style={{ fontSize: 18, color: '#0ea5e9' }} />
              {editingId === 'new'
                ? (pt
                    ? (mode === 'drilldown' ? `Novo posto em ${fcName}` : 'Novo posto')
                    : (mode === 'drilldown' ? `New site in ${fcName}` : 'New site'))
                : (pt ? 'Editar posto' : 'Edit site')}
            </Typography>
            {/* Picker de cliente final visível só fora do drilldown */}
            {mode === 'list' && (
              <FormControl size="small" variant="outlined" disabled={editingId !== 'new'}>
                <InputLabel>{pt ? 'Cliente final *' : 'Final customer *'}</InputLabel>
                <Select
                  label={pt ? 'Cliente final *' : 'Final customer *'}
                  value={form.finalCustomerId || ''}
                  onChange={e => setForm({ ...form, finalCustomerId: e.target.value })}
                >
                  {customers.filter(c => c.isActive).map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box className={classes.formRow}>
              <TextField
                label={pt ? 'Nome *' : 'Name *'}
                size="small" variant="outlined"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                autoFocus
              />
              <TextField
                label={pt ? 'Código interno' : 'Internal code'}
                size="small" variant="outlined"
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value })}
              />
            </Box>
            <Box>
              <AddressAutocomplete
                label={pt ? 'Morada' : 'Address'}
                value={form.address}
                onChange={(val) => setForm(f => ({ ...f, address: val }))}
                onSelect={(item) => {
                  // Sugestão escolhida: preenche morada + lat + lng automaticamente.
                  // Mantém o operador como dono final dos valores — pode ajustar depois.
                  const [lng, lat] = item.coordinates || [];
                  setForm(f => ({
                    ...f,
                    address: item.name,
                    lat: lat != null ? String(lat.toFixed(6)) : f.lat,
                    lng: lng != null ? String(lng.toFixed(6)) : f.lng,
                  }));
                }}
                country="AO"
                placeholder={pt ? 'Começa a escrever e escolhe uma sugestão…' : 'Start typing and pick a suggestion…'}
              />
              {form.lat && form.lng && (
                <Box className={classes.coordsHint}>
                  <RoomIcon style={{ fontSize: 12 }} />
                  {pt ? 'Coordenadas preenchidas: ' : 'Coordinates filled: '}
                  <span style={{ fontFamily: 'monospace' }}>{form.lat}, {form.lng}</span>
                </Box>
              )}
            </Box>
            <Box className={classes.formRow}>
              <TextField
                label={pt ? 'Latitude (-90 a 90)' : 'Latitude (-90 to 90)'}
                size="small" variant="outlined"
                value={form.lat}
                onChange={e => setForm({ ...form, lat: e.target.value })}
                placeholder="-8.838333"
                helperText={pt ? 'Auto-preenchido pela sugestão' : 'Auto-filled by suggestion'}
              />
              <TextField
                label={pt ? 'Longitude (-180 a 180)' : 'Longitude (-180 to 180)'}
                size="small" variant="outlined"
                value={form.lng}
                onChange={e => setForm({ ...form, lng: e.target.value })}
                placeholder="13.234444"
                helperText={pt ? 'Auto-preenchido pela sugestão' : 'Auto-filled by suggestion'}
              />
            </Box>
            <TextField
              label={pt ? 'Notas internas' : 'Internal notes'}
              size="small" variant="outlined"
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
            <Box className={classes.formActions}>
              <Button
                onClick={cancelEdit}
                style={{ textTransform: 'none', fontSize: '0.78rem' }}
                disabled={saving}
              >
                {pt ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button
                onClick={saveForm}
                disabled={saving}
                className={classes.saveButton}
                startIcon={saving ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <CheckIcon style={{ fontSize: 16 }} />}
              >
                {pt ? 'Guardar' : 'Save'}
              </Button>
            </Box>
          </Box>
        )}

        {loading && sites.length === 0 ? (
          <Box className={classes.empty}>
            <CircularProgress size={32} style={{ color: '#3b82f6' }} />
            <Typography style={{ fontSize: '0.8rem' }}>
              {pt ? 'A carregar…' : 'Loading…'}
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box className={classes.empty}>
            <LocalGasStationIcon style={{ fontSize: 48, color: '#cbd5e1' }} />
            <Typography style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {search
                ? (pt ? 'Sem resultados para a pesquisa.' : 'No results for search.')
                : mode === 'drilldown'
                  ? (pt ? `Sem postos para ${fcName}.` : `No sites for ${fcName}.`)
                  : (pt ? 'Ainda não há postos.' : 'No sites yet.')}
            </Typography>
            {!search && customers.length > 0 && (
              <Button
                className={classes.addButton}
                startIcon={<AddIcon style={{ fontSize: 18 }} />}
                onClick={startCreate}
                disabled={editingId !== null}
              >
                {pt ? 'Criar primeiro' : 'Create first one'}
              </Button>
            )}
            {mode === 'list' && customers.length === 0 && (
              <Typography style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                {pt ? 'Cria primeiro um cliente final.' : 'Create a final customer first.'}
              </Typography>
            )}
          </Box>
        ) : (
          filtered.map(s => {
            const hasCoords = s.lat != null && s.lng != null;
            return (
              <Box
                key={s.id}
                className={`${classes.card} ${!s.isActive ? classes.cardInactive : ''}`}
              >
                <Box className={classes.cardIcon}>
                  <LocalGasStationIcon style={{ fontSize: 20, color: '#0ea5e9' }} />
                </Box>
                <Box className={classes.cardMain}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Typography className={classes.cardName}>{s.name}</Typography>
                    {mode === 'list' && s.finalCustomerName && (
                      <Chip
                        label={s.finalCustomerName}
                        size="small"
                        style={{
                          height: 18, fontSize: '0.6rem', fontWeight: 700,
                          background: '#fee2e2', color: '#b91c1c',
                        }}
                      />
                    )}
                    {s.code && (
                      <Chip
                        label={s.code}
                        size="small"
                        style={{ height: 18, fontSize: '0.6rem', background: '#e2e8f0', color: '#475569', fontWeight: 700 }}
                      />
                    )}
                    {!s.isActive && (
                      <Chip
                        label={pt ? 'Desactivado' : 'Inactive'}
                        size="small"
                        style={{ height: 18, fontSize: '0.6rem', background: '#e2e8f0', color: '#475569' }}
                      />
                    )}
                  </Box>
                  <Box className={classes.cardMeta}>
                    {s.address && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        <RoomIcon style={{ fontSize: 12 }} />
                        {s.address}
                      </span>
                    )}
                    {hasCoords && (
                      <span style={{ fontFamily: 'monospace', fontSize: '0.68rem' }}>
                        {s.lat.toFixed(5)}, {s.lng.toFixed(5)}
                      </span>
                    )}
                    {s.equipmentCount > 0 && (
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>
                        {s.equipmentCount} {pt ? 'equipamentos' : 'equipments'}
                      </span>
                    )}
                  </Box>
                </Box>
                <Box className={classes.cardActions}>
                  <Tooltip title={pt ? 'Ver equipamentos' : 'View equipments'}>
                    <Button
                      size="small"
                      onClick={() => {
                        // Em modo list, usa-se o cliente final vindo da própria linha
                        dispatch(setSelectedCustomerSite({
                          id: s.id, name: s.name,
                          finalCustomerId: s.finalCustomerId || fcId,
                          finalCustomerName: s.finalCustomerName || fcName,
                        }));
                        dispatch(setActiveView('siteEquipments'));
                      }}
                      endIcon={<ChevronRightIcon style={{ fontSize: 16 }} />}
                      startIcon={<BuildIcon style={{ fontSize: 14 }} />}
                      style={{
                        textTransform: 'none', fontSize: '0.7rem', fontWeight: 700,
                        color: '#3b82f6', background: '#eff6ff',
                        padding: '4px 10px', borderRadius: 8,
                      }}
                    >
                      {pt ? 'Equipamentos' : 'Equipments'}{s.equipmentCount > 0 ? ` (${s.equipmentCount})` : ''}
                    </Button>
                  </Tooltip>
                  <Tooltip title={s.isActive ? (pt ? 'Desactivar' : 'Deactivate') : (pt ? 'Reactivar' : 'Reactivate')}>
                    <IconButton size="small" onClick={() => toggleActive(s)}>
                      {s.isActive
                        ? <CloseIcon style={{ fontSize: 18, color: '#94a3b8' }} />
                        : <CheckIcon style={{ fontSize: 18, color: '#22c55e' }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={pt ? 'Editar' : 'Edit'}>
                    <IconButton size="small" onClick={() => startEdit(s)} disabled={editingId !== null}>
                      <EditIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={pt ? 'Eliminar (soft)' : 'Delete (soft)'}>
                    <IconButton size="small" onClick={() => setDeleteConfirm({ id: s.id, name: s.name })}>
                      <DeleteIcon style={{ fontSize: 16, color: '#ef4444' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      <ConfirmDialog
        open={!!deleteConfirm}
        title={pt ? 'Desactivar posto?' : 'Deactivate site?'}
        message={deleteConfirm
          ? (pt
            ? `"${deleteConfirm.name}" passa a desactivado. Os equipamentos e OTs já criados mantêm a referência. Pode ser reactivado em qualquer momento.`
            : `"${deleteConfirm.name}" will be deactivated. Existing equipments and OTs keep the reference. Can be reactivated at any time.`)
          : ''}
        severity="warning"
        confirmLabel={pt ? 'Desactivar' : 'Deactivate'}
        cancelLabel={pt ? 'Cancelar' : 'Cancel'}
        onConfirm={confirmDeleteNow}
        onClose={() => setDeleteConfirm(null)}
      />
    </Box>
  );
}

export default CustomerSitesView;
